import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, TextField, IconButton, CircularProgress, Chip } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import SendIcon from "@mui/icons-material/Send";
import { student_analysis_chat_api } from "../DAL/SchoolPortal/SchoolPortal";
import {
  surfaceRaised, border, textPrimary, textSecondary, textMuted,
  accent, font, inputSx, easing,
} from "../layouts/schoolPortal/schoolPortalTheme";

const SUGGESTIONS = [
  "What should I say to them today?",
  "Are they ready to sit an exam?",
  "Is this a real misconception or just careless mistakes?",
  "Have they gotten better or worse recently?",
];

// The chat's system prompt tells the model to use **bold** for key numbers/
// topics and it writes the occasional "- " bullet list — neither rendered as
// anything but literal asterisks before, since messages went through plain
// Typography. This is a small, dependency-free parser for just those two
// patterns (not a full markdown engine — the model doesn't produce anything
// fancier than this).
function renderInlineBold(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
  );
}

function MessageBody({ text, textColor }) {
  const lines = text.split("\n");
  const blocks = [];
  let listBuffer = [];

  const flushList = (key) => {
    if (listBuffer.length) {
      blocks.push(
        <Box key={`list-${key}`} component="ul" sx={{ m: 0, mt: 0.4, mb: 0.4, pl: 2.25 }}>
          {listBuffer.map((item, i) => (
            <Typography key={i} component="li" sx={{ color: textColor, fontSize: "13.5px", lineHeight: 1.55, fontFamily: font }}>
              {renderInlineBold(item, `li-${key}-${i}`)}
            </Typography>
          ))}
        </Box>
      );
      listBuffer = [];
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    const bulletMatch = /^[-*]\s+(.*)/.exec(trimmed);
    if (bulletMatch) {
      listBuffer.push(bulletMatch[1]);
      return;
    }
    flushList(i);
    if (trimmed) {
      blocks.push(
        <Typography key={i} sx={{ color: textColor, fontSize: "13.5px", lineHeight: 1.55, fontFamily: font, mt: i > 0 ? 0.4 : 0 }}>
          {renderInlineBold(line, `p-${i}`)}
        </Typography>
      );
    }
  });
  flushList("end");

  return <>{blocks}</>;
}

export default function StudentAIChat({ studentId, studentName, compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    // Scroll the message list, not the page: scrollIntoView moved the whole
    // record to wherever the chat sat, including on first render.
    const box = listRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages]);

  useEffect(() => { setMessages([]); setInput(""); }, [studentId]);

  const handleSend = async (text) => {
    const userMessage = (text ?? input).trim();
    if (!userMessage || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);
    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const response = await student_analysis_chat_api(userMessage, studentId, history);
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: response?.response || response?.message || "Sorry, I couldn't process that request.",
      }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Something went wrong processing that request." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: compact ? 1 : 1 }}>
        <ChatIcon sx={{ color: accent, fontSize: compact ? 15 : 15 }} />
        <Typography sx={{ color: textSecondary, fontSize: compact ? "12px" : "12.5px", fontWeight: 700, fontFamily: font }}>
          Ask AI about {studentName?.split(" ")[0] || "this student"}
        </Typography>
      </Box>

      {messages.length === 0 && !compact && (
        <Box sx={{ mb: 1.25 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
            {SUGGESTIONS.map((q) => (
              <Chip
                key={q}
                label={q}
                size="small"
                onClick={() => handleSend(q)}
                sx={{
                  backgroundColor: surfaceRaised, color: textSecondary, cursor: "pointer",
                  fontFamily: font, fontSize: "12.5px", height: 26, border: `1px solid ${border}`,
                  transition: `all 0.15s ${easing}`,
                  "&:hover": { backgroundColor: "#fbe4e6", color: accent, borderColor: "#f3c4c9" },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box ref={listRef} sx={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.25, minHeight: 0, maxHeight: compact ? 120 : 420, mb: compact ? 1 : 1.25 }}>
        {messages.map((msg, i) => (
          <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <Typography sx={{ color: textMuted, fontSize: "10px", fontWeight: 700, fontFamily: font, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.3, px: 0.5 }}>
              {msg.role === "user" ? "You" : "AI"}
            </Typography>
            <Box sx={{
              maxWidth: "88%", py: 1, px: 1.5,
              borderRadius: msg.role === "user" ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
              // Solid colors, not the theme's transparent accentSoft — this panel can sit
              // on a dark subject-gradient background, and a translucent tint over a dark
              // background renders dark itself, making the near-black message text unreadable.
              backgroundColor: msg.role === "user" ? "#fbe4e6" : surfaceRaised,
              border: `1px solid ${msg.role === "user" ? "#f3c4c9" : border}`,
            }}>
              <MessageBody text={msg.content} textColor={textPrimary} />
            </Box>
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CircularProgress size={14} sx={{ color: accent }} />
            <Typography sx={{ color: textMuted, fontSize: "12.5px", fontFamily: font }}>Thinking…</Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth size="small" placeholder="Ask a question…" value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          sx={{ ...inputSx, "& .MuiInputBase-input": { fontSize: "13.5px" } }}
        />
        <IconButton
          onClick={() => handleSend()}
          disabled={!input.trim() || loading}
          sx={{
            backgroundColor: accent, color: "#fff", flexShrink: 0,
            "&:hover": { backgroundColor: "#a81828" },
            "&.Mui-disabled": { backgroundColor: surfaceRaised, color: textMuted },
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
